import { TimeSuggestionFilters } from "@/components/analysis/time-suggestion-filters";
import {
  type TimeQueueTab,
  TimeSuggestionQueue,
} from "@/components/analysis/time-suggestion-queue";
import { api } from "@/lib/api";
import { paginationSearchFields } from "@/lib/search";
import {
  Link,
  Navigate,
  createFileRoute,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/suggestions/time-entries-all` — the **whole team's** proposed time entries,
 * for admins and accountants.
 *
 * Separate from the personal queue rather than a filter on it, because the two
 * are different jobs: on your own page you confirm your hours, here you review
 * everyone's. It's also the only place a proposal whose owner couldn't be
 * matched shows up at all — nobody else can see it, so nobody else can resolve
 * it, which is why "nobody assigned" is one of the person filter's options.
 *
 * The filters differ from the personal page for the same reason: here you're
 * answering "whose, on what, when", not working through your own week.
 *
 * E2E behaviour contract lives in `e2e/suggestions/time-suggestions.spec.ts`.
 */

const searchSchema = z.object({
  status: z.enum(["pending", "approved", "rejected", "all"]).optional(),
  projectId: z.string().optional(),
  userId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  ...paginationSearchFields,
});

export const Route = createFileRoute("/_app/suggestions/time-entries-all")({
  validateSearch: searchSchema,
  component: TeamTimeSuggestionsPage,
});

function TeamTimeSuggestionsPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [users, setUsers] = useState<
    Array<{ id: string; name: string | null; email: string; former?: boolean }>
  >([]);

  const seesEveryone = user.globalRole === "admin" || user.globalRole === "accountant";

  useEffect(() => {
    if (!seesEveryone) return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the project filter just stays empty */
      });
    api
      .get<{ users: Array<{ id: string; name: string | null; email: string; former?: boolean }> }>(
        "/api/time-entries/people",
      )
      .then((res) => setUsers(res.users))
      .catch(() => {
        /* the person filter just stays empty */
      });
  }, [seesEveryone]);

  // The server scopes the list anyway (a plain employee asking for "all" gets
  // their own); this just keeps the page from pretending to be something it
  // isn't for them.
  if (!seesEveryone) return <Navigate to="/suggestions/time-entries" />;

  const tab: TimeQueueTab = search.status ?? "pending";

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t("timeEntries.suggestionsAll.title")}</h1>
        <p className="text-muted-foreground mt-1">
          <Trans
            i18nKey="timeEntries.suggestionsAll.subtitle"
            components={{ 1: <Link to="/suggestions/time-entries" className="underline" /> }}
          />
        </p>
      </div>

      <TimeSuggestionFilters
        values={search}
        onChange={(next) =>
          // Narrowing the list makes the page you were on meaningless — back
          // to the first one, or a filter can land you on an empty page.
          navigate({
            to: "/suggestions/time-entries-all",
            search: {
              ...next,
              status: search.status,
              pageSize: search.pageSize,
              page: undefined,
            },
          })
        }
        projects={projects}
        users={users}
      />

      <TimeSuggestionQueue
        tab={tab}
        onTabChange={(next) =>
          navigate({
            to: "/suggestions/time-entries-all",
            search: { ...search, status: next === "pending" ? undefined : next, page: undefined },
          })
        }
        projects={projects}
        scope="all"
        showOwner
        canPair={user.globalRole === "admin"}
        filters={search}
        page={search.page}
        pageSize={search.pageSize}
        basePath="/suggestions/time-entries-all"
        searchParams={{
          status: search.status,
          projectId: search.projectId,
          userId: search.userId,
          from: search.from,
          to: search.to,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
